import EmojiPicker from 'emoji-picker-react';
import PropTypes from 'prop-types';
import { memo, useRef, useState } from 'react';
import { Button, Overlay } from 'react-bootstrap';

const EditorToolbar = memo(({
  // Text content and handlers
  textValue = '',
  textInputRef,
  onTextChange,
  onTextFormat,
  
  // Features to enable/disable
  showEmojiPicker = true,
  showCharacterCount = false,
  showAddVariable = false,
  showAddSnippet = false,
  showAddNote = false,
  
  // Character limits and counts
  maxCharacters = 1024,
  characterCount,
  
  // Variable functionality
  variableCount = 0,
  maxVariables = 3,
  onAddVariable,
  
  // Action handlers
  onAddSnippet,
  onAddNote,
  
  // Styling variants
  variant = 'template',
  
  // Disabled state
  disabled = false,
  
  // Custom styling
  className = '',
  style = {}
}) => {
  const [showEmojiPickerState, setShowEmojiPickerState] = useState(false);
  const emojiButtonRef = useRef(null);

  // Extract text formatting logic from TemplateForm
  const handleTextFormat = (formatType) => {
    if (!textInputRef?.current || !onTextChange || disabled) return;

    const textarea = textInputRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textValue;
    const selectedText = text.substring(start, end);

    let formatMarkers;
    switch (formatType) {
      case 'bold':
        formatMarkers = ['*', '*'];
        break;
      case 'italic':
        formatMarkers = ['_', '_'];
        break;
      case 'strikethrough':
        formatMarkers = ['~', '~'];
        break;
      case 'monospace':
        formatMarkers = ['```', '```'];
        break;
      default:
        return;
    }

    let newText;
    let newCursorPos;

    if (start === end) {
      newText =
        text.substring(0, start) + formatMarkers[0] + formatMarkers[1] + text.substring(end);
      newCursorPos = start + formatMarkers[0].length;
    } else {
      newText =
        text.substring(0, start) +
        formatMarkers[0] +
        selectedText +
        formatMarkers[1] +
        text.substring(end);
      newCursorPos = end + formatMarkers[0].length + formatMarkers[1].length;
    }

    onTextChange(newText);

    setTimeout(() => {
      textarea.focus();
      if (start === end) {
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      } else {
        const selectionStart = start + formatMarkers[0].length;
        const selectionEnd = end + formatMarkers[0].length;
        textarea.setSelectionRange(selectionStart, selectionEnd);
      }
    }, 0);

    // Call custom handler if provided
    if (onTextFormat) {
      onTextFormat(formatType);
    }
  };

  const handleEmojiClick = (emojiData) => {
    if (!textInputRef?.current || !onTextChange || disabled) return;

    const textarea = textInputRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textValue;
    const newText = text.substring(0, start) + emojiData.emoji + text.substring(end);
    onTextChange(newText);
    setShowEmojiPickerState(false);
  };

  // SVG Icons
  const SmileIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" fill="none" stroke="#9CA8BB" strokeWidth="2" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" fill="none" stroke="#9CA8BB" strokeWidth="2" />
      <circle cx="9" cy="9" r="1" fill="#9CA8BB" />
      <circle cx="15" cy="9" r="1" fill="#9CA8BB" />
    </svg>
  );

  const BoldIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" fill="#9CA8BB" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" fill="#9CA8BB" />
    </svg>
  );

  const ItalicIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <line x1="19" y1="4" x2="10" y2="4" stroke="#9CA8BB" strokeWidth="2" />
      <line x1="14" y1="20" x2="5" y2="20" stroke="#9CA8BB" strokeWidth="2" />
      <line x1="15" y1="4" x2="9" y2="20" stroke="#9CA8BB" strokeWidth="2" />
    </svg>
  );

  const StrikethroughIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <path d="M16 4H9a3 3 0 0 0-2.83 4" fill="none" stroke="#9CA8BB" strokeWidth="2" />
      <path d="M14 12a4 4 0 0 1 0 8H6" fill="none" stroke="#9CA8BB" strokeWidth="2" />
      <line x1="4" y1="12" x2="20" y2="12" stroke="#9CA8BB" strokeWidth="2" />
    </svg>
  );

  const CodeIcon = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
      <polyline points="16,18 22,12 16,6" fill="none" stroke="#9CA8BB" strokeWidth="2" />
      <polyline points="8,6 2,12 8,18" fill="none" stroke="#9CA8BB" strokeWidth="2" />
    </svg>
  );

  const PlusIcon = () => (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
      <line x1="12" y1="6" x2="12" y2="18" stroke="#737C8A" strokeWidth="2" strokeLinecap="round" />
      <line x1="6" y1="12" x2="18" y2="12" stroke="#737C8A" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  // Base styles matching Figma design
  const baseStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '15px',
    ...style
  };

  const textFormattingStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px'
  };

  const buttonStyles = variant === 'template' ? {
    variant: "light",
    className: "btn-icon"
  } : {
    variant: "light",
    style: {
      width: '14px',
      height: '14px',
      padding: '0',
      border: 'none',
      backgroundColor: 'transparent',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }
  };

  const actionButtonStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '3px',
    padding: '4px 8px',
    border: 'none',
    backgroundColor: 'transparent'
  };

  const actionButtonIconStyles = {
    width: '14px',
    height: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  const actionButtonTextStyles = {
    fontFamily: 'Poppins',
    fontWeight: '400',
    fontSize: '12px',
    lineHeight: '12px', // Match the icon height for better alignment
    color: '#667085',
    display: 'flex',
    alignItems: 'center'
  };

  return (
    <div className={`editor-toolbar ${className}`} style={baseStyles}>
      {/* Text formatting icons */}
      <div style={textFormattingStyles}>
        {/* Emoji picker */}
        {showEmojiPicker && (
          <>
            <Button
              ref={emojiButtonRef}
              {...buttonStyles}
              onClick={() => setShowEmojiPickerState(!showEmojiPickerState)}
              disabled={disabled}
            >
              <SmileIcon />
            </Button>
            <Overlay
              target={emojiButtonRef.current}
              show={showEmojiPickerState}
              placement="top"
              rootClose
              onHide={() => setShowEmojiPickerState(false)}
            >
              {({ ...props }) => (
                <div
                  {...props}
                  style={{
                    ...props.style,
                    padding: '5px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                  }}
                >
                  <EmojiPicker onEmojiClick={handleEmojiClick} width={300} height={400} />
                </div>
              )}
            </Overlay>
          </>
        )}

        {/* Bold */}
        <Button
          {...buttonStyles}
          onClick={() => handleTextFormat('bold')}
          title="Make text bold (*text*)"
          disabled={disabled}
        >
          <BoldIcon />
        </Button>

        {/* Italic */}
        <Button
          {...buttonStyles}
          onClick={() => handleTextFormat('italic')}
          title="Make text italic (_text_)"
          disabled={disabled}
        >
          <ItalicIcon />
        </Button>

        {/* Strikethrough */}
        <Button
          {...buttonStyles}
          onClick={() => handleTextFormat('strikethrough')}
          title="Make text strikethrough (~text~)"
          disabled={disabled}
        >
          <StrikethroughIcon />
        </Button>

        {/* Code */}
        <Button
          {...buttonStyles}
          onClick={() => handleTextFormat('monospace')}
          title="Format as code (```text```)"
          disabled={disabled}
        >
          <CodeIcon />
        </Button>
      </div>

      {/* Add Variable button - only for template variant */}
      {showAddVariable && variableCount < maxVariables && (
        <Button 
          variant="light" 
          onClick={onAddVariable} 
          className="add-variable-btn"
          style={actionButtonStyles}
          disabled={disabled}
        >
          <div style={actionButtonIconStyles}>
            <PlusIcon />
          </div>
          <span style={actionButtonTextStyles}>Add Variable</span>
        </Button>
      )}

      {/* Add Snippet button - only for message variant */}
      {showAddSnippet && (
        <Button
          variant="light"
          onClick={onAddSnippet}
          style={actionButtonStyles}
          disabled={disabled}
        >
          <div style={actionButtonIconStyles}>
            <PlusIcon />
          </div>
          <span style={actionButtonTextStyles}>Add Snippet</span>
        </Button>
      )}

      {/* Add Note button - only for message variant */}
      {showAddNote && (
        <Button
          variant="light"
          onClick={onAddNote}
          style={actionButtonStyles}
          disabled={disabled}
        >
          <div style={actionButtonIconStyles}>
            <PlusIcon />
          </div>
          <span style={actionButtonTextStyles}>Add Note</span>
        </Button>
      )}

      {/* Character count - only for template variant */}
      {showCharacterCount && (
        <div className="char-count">
          {characterCount || textValue.length}/{maxCharacters} characters
        </div>
      )}
    </div>
  );
});

EditorToolbar.propTypes = {
  // Text content and handlers
  textValue: PropTypes.string,
  textInputRef: PropTypes.object,
  onTextChange: PropTypes.func,
  onTextFormat: PropTypes.func,
  
  // Features to enable/disable
  showEmojiPicker: PropTypes.bool,
  showCharacterCount: PropTypes.bool,
  showAddVariable: PropTypes.bool,
  showAddSnippet: PropTypes.bool,
  showAddNote: PropTypes.bool,
  
  // Character limits and counts
  maxCharacters: PropTypes.number,
  characterCount: PropTypes.number,
  
  // Variable functionality
  variableCount: PropTypes.number,
  maxVariables: PropTypes.number,
  onAddVariable: PropTypes.func,
  
  // Action handlers
  onAddSnippet: PropTypes.func,
  onAddNote: PropTypes.func,
  
  // Styling variants
  variant: PropTypes.oneOf(['template', 'message']),
  
  // Disabled state
  disabled: PropTypes.bool,
  
  // Custom styling
  className: PropTypes.string,
  style: PropTypes.object
};

EditorToolbar.displayName = 'EditorToolbar';

export default EditorToolbar;